import UpdateTemplatesView from '@powerednow/app/FieldServices/view/modal/updateTemplates/updateTemplatesView';
import ROLES from '@powerednow/shared/constants/roles';
import { default as Bluebird } from 'bluebird';
import * as _ from 'lodash';
import { BaseDescriptor } from './baseDescriptor';

declare let Ext: any;
declare let Utils: any;
declare let L: any;
declare let Deft: any;
declare let DataWrapper: any;
export default class FormTemplateChange extends BaseDescriptor {
    introducedIn = '9.0.0';

    affectedStore = 'CompanyForm';

    name = 'FormTemplateChange';

    dependencyStores = ['Form'];

    configFieldMap = {
        /*email_config: { Disabled in PN-11232 - will be reactivated later
            subject: 'emailSubjectField',
            body: 'emailBodyField',
        },*/
        reminder_config: {
            reminderActivated: 'reminderActivatedOption',
            subject: 'reminderSubjectField',
            body: 'reminderBodyField',
            smsText: 'reminderSMSTextField',
            sendReminderAfterXDays: 'reminderSendAfterDaysField',
            sendReminderByEmail: 'reminderEmailOption',
            sendReminderByText: 'reminderTextOption',
            sendNotification: 'notificationOption',
            notifyUsersXDaysBefore: 'reminderNotifyUsersDaysBeforeField',
        },
    };

    openTemplateUpdateBtn = null;

    buttonVisibilityHandler = (e: any) => {
        const { hash } = e.target.location;
        this.openTemplateUpdateBtn.setHidden(hash !== '#settings/formSettings');
    };

    private generateRules() {
        if (!this.rules.length) {
            const companyFormStore = Ext.getStore('CompanyForm');
            const formStore = Ext.getStore('Form')
            companyFormStore.getRecords().forEach((companyForm) => {
                const formType = companyForm.get('form_type_id')
                const latestFormVersion = formStore.getLatestFormByTypeId(formType)?.get('version');
                // const emailConfig = companyForm.get('email_config'); - Disabled in PN-11232 - will be reactivated later
                const reminderConfig = companyForm.get('reminder_config');
                // this.createRuleForConfig(companyForm.get('id'),emailConfig, formType, latestFormVersion, 'email_config'); - Disabled in PN-11232 - will be reactivated later
                this.createRuleForConfig(companyForm.get('id'),reminderConfig, formType, latestFormVersion, 'reminder_config');

            });
        }
    }

    private createRuleForConfig(companyFormId, config, formType, latestFormVersion, configType) {
        if (config && !_.isEmpty(config)) {
            const currentVersion = config.form_version;
            const { isEmailConfigChanged, isReminderConfigChanged } = Ext.getStore('Form').getFormSettingsChanges(formType, currentVersion, latestFormVersion);
            const needsUpdate = (configType === 'email_config' && isEmailConfigChanged) || (configType === 'reminder_config' && isReminderConfigChanged);
            if (needsUpdate) {
                this.rules.push(() => {
                    const isOutDated = config.form_version !== latestFormVersion;
                    const breakRecord = this.createChangeRecord({
                        companyFormId,
                        formType,
                        configType,
                        targetVersion: latestFormVersion,
                    });
                    return {
                        created: isOutDated ? breakRecord : undefined,
                        fixed: isOutDated ? undefined : breakRecord,
                    };
                });
            }
        }
    }

    async showAlertNotification() {
        const permissionService = Deft.Injector.resolve('permissionService');
        const isEditor = permissionService.canCurrentUserDo(ROLES.PERMISSIONS.CAN_EDIT_FORM_SETTINGS);
        const dontShowAgain = !isEditor ? { dontShowAgain: { dialogId: `${this.name}-dismissableDialog` } } : undefined;
        const editorMessage = L(
            'FormTemplateChange.editorAlert',
            `In the latest version of the app, some form templates have been updated, and your previous email or reminder settings might not be valid.<br/>
             Your have to review all affected form settings.<br/>
             Do you want to do this now?<br/>
             <i style="color: red"><small>Please note: If you do this later, the affected forms will use their new default templates instead of your old custom template!</small></i>`,
        );
        const editorButtons = [
            {
                text: 'No',
                cls: 'button-back',
            }, {
                text: 'Yes',
                cls: 'button-proceed',
            },
        ];
        const pressedButton = await Ext.Msg.showAsync({
            title: 'Warning',
            ...dontShowAgain,
            message: isEditor ? editorMessage : L(
                'FormTemplateChange.defaultAlert',
                `In the latest version of the app, some form templates have been updated, and your previous email or reminder settings might not be valid.<br/>
                 Until someone in your company updates the form settings, the affected form types will be sent with their new defaults`,
            ),
            buttons: isEditor ? editorButtons : [{
                text: 'OK',
                cls: 'button-proceed',
            }],
        });
        if (pressedButton === 'Yes') {
            await this.showTemplateUpdateModal();
        }
        if (pressedButton === 'No') {
            this.emit('notificationClosed');
        }
    }

    async createItemConfig(breakRecord) {
        const { formType, configType, companyFormId } = breakRecord.get('details');
        const config = Ext.getStore('CompanyForm').getById(companyFormId).get(configType);
        const formStore = Ext.getStore('Form');
        const latestFormVersion = formStore.getLatestFormByTypeId(formType);
        const component = `FieldServices.widgets.form.setting.configuration.${configType === 'email_config' ? 'emailSettings.EmailSettings' : 'reminderSettings.ReminderSettings'}`;
        const form = await DataWrapper.getComplexDataObject('Form', latestFormVersion.data.id);
        const formManager = await form.getFormManager();
        const currentDefaultForm = {
            getEmailConfig: () => formManager.getSettings().emailConfig,
            getReminderConfig: () => formManager.getSettings().reminderConfig,
            data: { id: companyFormId },
        };
        const oldForm = {
            getEmailConfig: () => config,
            getReminderConfig: () => config,
            data: { id: companyFormId },
        };
        const editorConfig = {
            component,
            creationConfig: {
                title: latestFormVersion.get('title') + (configType === 'email_config' ? ' - Email Settings' : ' - Reminder Settings'),
            },
            afterCreate: editor => editor.getController().configure({ companyForm: currentDefaultForm }),
            bodyItemId: '#terminologyFieldset',
            setOldValuesFn: editor => editor.getController().configure({ companyForm: oldForm }),
            setDefaultValuesFn: editor => editor.getController().configure({ companyForm: currentDefaultForm }),
            saveFn: editor => editor.getController().save(),
        };
        const tooltipConfigs = Object.keys(config).map(configKey => ({
            value: config[configKey],
            attachToId: `#${this.configFieldMap[configType][configKey]}`,
        }));
        return {
            editorConfig,
            tooltipConfigs,
            breakRecord,
        };
    }

    async showTemplateUpdateModal(rerunCheck = false) {
        if (rerunCheck) {
            await this.runCheck(true);
        }
        const breakRecordStore = Ext.getStore('BreakingChange');
        const existingRecords = breakRecordStore.getRecordsByFilter('originDescriptor', this.name).sort((r1, r2) => r1.get('details').formType - r2.get('details').formType);
        if (existingRecords.length) {
            const popup = new UpdateTemplatesView({
                controllerConfig: {
                    itemConfigs: await Bluebird.map(existingRecords, async breakRecord => await this.createItemConfig(breakRecord)),
                },
                listeners: {
                    templatesUpdated: (allSaved: boolean) => {
                        Utils.showNotifyBox(L('Settings.formTemplatesUpdated', 'Form templates updated successfully.'));
                        if (!allSaved) {
                            this.createFloatingButton();
                        }
                    },
                    modalClose: () => {
                        this.createFloatingButton();
                    },
                },
            });

            Ext.Viewport.add(popup);
            popup.show();
        } else if (this.openTemplateUpdateBtn) {
            this.openTemplateUpdateBtn.destroy();
            window.removeEventListener('hashchange', this.buttonVisibilityHandler);
            Utils.showNotifyBox(L('formTemplateChange.noRecordsInfo', 'All affected templates updated'), 'notib-desc');
        }
    }

    createFloatingButton() {
        if (Ext.getStore('BreakingChange').getRecordsByFilter('originDescriptor', this.name).length) {
            const isOnSettingsPage = window.location.hash !== '#settings/formSettings';
            this.openTemplateUpdateBtn = Ext.create({
                xtype: 'button',
                cls: 'Button-delete-noicon',
                text: 'Update outdated form templates',
                floating: true,
                hidden: isOnSettingsPage,
                bottom: '10px',
                right: '10px',
                handler: button => {
                    this.showTemplateUpdateModal(true).then(() => {
                        button.destroy();
                        window.removeEventListener('hashchange', this.buttonVisibilityHandler);
                    });
                },
            });
            Ext.Viewport.add(this.openTemplateUpdateBtn);
            window.addEventListener('hashchange', this.buttonVisibilityHandler);
            if (isOnSettingsPage) {
                Utils.showNotifyBox(L('formTemplateChange.buttonInfo', 'You can access the template update interface with the "Update outdated form templates" button on the Forms&Certificates settings page.'), 'notib-desc');
            }
        }
        this.emit('notificationClosed');
    }

    async runCheck(supressAlert): Promise<void> {
        this.generateRules();
        await super.runCheck(supressAlert);
    }
}
